import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Logout,
  Notifications,
  TrendingUp,
  TrendingDown,
  Remove,
  ArrowBack,
  FileDownload,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE_URL = 'https://apps.acom.edu.au/api';

// Color palette
const COLORS = {
  pending: '#FF9800',
  approved: '#4CAF50',
  rejected: '#F44336',
  hold: '#9E9E9E',
  'in-progress': '#2196F3',
  completed: '#8BC34A',
};

const CHART_COLORS = ['#1976d2', '#2196F3', '#42A5F5', '#64B5F6', '#90CAF9', '#BBDEFB'];

const Reports = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState('all'); // all, 7days, 30days, 90days

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/reports/executive-overview`, {
        params: { dateRange }
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup();
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const exportToCSV = () => {
    if (!reportData) return;

    const { requestsByStatus, requestsByType, volumeTrend, processingTime } = reportData;

    // Prepare CSV data
    let csvContent = "ACOM Apps Portal - Executive Overview Report\n";
    csvContent += `Date Range: ${reportData.summary.dateRangeText}\n`;
    csvContent += `Generated: ${new Date().toLocaleDateString()}\n\n`;

    // Summary stats
    csvContent += "SUMMARY STATISTICS\n";
    csvContent += `Total Requests,${reportData.summary.totalRequests}\n`;
    csvContent += `Pending Requests,${reportData.summary.pendingRequests}\n`;
    csvContent += `Approval Rate,${reportData.approvalRate.rate}%\n`;
    csvContent += `Average Processing Time,${reportData.processingTime.average} days\n\n`;

    // Requests by Status
    csvContent += "REQUESTS BY STATUS\n";
    csvContent += "Status,Count\n";
    requestsByStatus.forEach(item => {
      csvContent += `${item.name},${item.value}\n`;
    });
    csvContent += "\n";

    // Requests by Type
    csvContent += "REQUESTS BY TYPE\n";
    csvContent += "Type,Count\n";
    requestsByType.forEach(item => {
      csvContent += `${item.name},${item.value}\n`;
    });
    csvContent += "\n";

    // Volume Trend
    csvContent += "REQUEST VOLUME TREND\n";
    csvContent += "Date,Count\n";
    volumeTrend.forEach(item => {
      csvContent += `${item.date},${item.count}\n`;
    });
    csvContent += "\n";

    // Processing Time by Type
    csvContent += "PROCESSING TIME BY TYPE\n";
    csvContent += "Type,Average Days\n";
    processingTime.byType.forEach(item => {
      csvContent += `${item.type},${item.avgDays}\n`;
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ACOM_Executive_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Report exported to CSV');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const {
    summary,
    requestsByStatus,
    requestsByType,
    volumeTrend,
    processingTime,
    approvalRate,
  } = reportData;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Reports & Analytics
          </Typography>
          <Button
            color="inherit"
            startIcon={<FileDownload />}
            onClick={exportToCSV}
            sx={{ mr: 2 }}
          >
            Export to CSV
          </Button>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 4, width: '100%', maxWidth: '100%' }}>
        {/* Page Title & Filters */}
        <Box sx={{ mb: 4, maxWidth: '100%' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
            Executive Overview
          </Typography>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="medium">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    label="Date Range"
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="7days">Last 7 Days</MenuItem>
                    <MenuItem value="30days">Last 30 Days</MenuItem>
                    <MenuItem value="90days">Last 90 Days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <Typography variant="body1" color="text.secondary">
                  Showing data from <strong>{summary.dateRangeText}</strong>
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <Card
              sx={{
                height: '100%',
                minHeight: 160,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  Total Requests
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {summary.totalRequests}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  All submitted requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card
              sx={{
                height: '100%',
                minHeight: 160,
                background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                color: 'white',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  Pending Requests
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {summary.pendingRequests}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Requires attention
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card
              sx={{
                height: '100%',
                minHeight: 160,
                background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                color: 'white',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  Approval Rate
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {approvalRate.rate}%
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {approvalRate.approved} of {approvalRate.total} completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card
              sx={{
                height: '100%',
                minHeight: 160,
                background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                color: 'white',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  Avg Processing Time
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {processingTime.average}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {processingTime.unit}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts - 2x2 Grid */}
        <Grid container spacing={3}>
          {/* Chart 1 - Status Distribution */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)', height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Requests by Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={requestsByStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius="60%"
                      innerRadius="0%"
                      fill="#8884d8"
                      dataKey="value"
                      style={{ fontSize: '12px', fontWeight: 600 }}
                    >
                      {requestsByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.name.toLowerCase()] || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        border: '2px solid #1976d2',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={50}
                      iconType="circle"
                      iconSize={10}
                      wrapperStyle={{ fontSize: '12px', paddingTop: '15px', fontWeight: 600 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Chart 2 - Request Types */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)', height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Requests by Type
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={requestsByType} margin={{ top: 15, right: 30, left: 20, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeWidth={1} />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 12, fontWeight: 600 }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fontWeight: 600 }}
                      allowDecimals={false}
                      width={50}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        border: '2px solid #1976d2',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                    />
                    <Bar
                      dataKey="value"
                      fill="#1976d2"
                      name="Requests"
                      radius={[6, 6, 0, 0]}
                      barSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Chart 3 - Volume Trend */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)', height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Request Volume Trend
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={volumeTrend} margin={{ top: 15, right: 30, left: 20, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeWidth={1} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fontWeight: 600 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fontWeight: 600 }}
                      allowDecimals={false}
                      width={50}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        border: '2px solid #1976d2',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ r: 4, fill: '#1976d2', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                      name="Requests"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Chart 4 - Processing Time */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)', height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Avg Processing Time by Type
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={processingTime.byType} margin={{ top: 15, right: 30, left: 20, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeWidth={1} />
                    <XAxis
                      dataKey="type"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 12, fontWeight: 600 }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fontWeight: 600 }}
                      allowDecimals={false}
                      width={50}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        border: '2px solid #2196F3',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                    />
                    <Bar
                      dataKey="avgDays"
                      fill="#2196F3"
                      name="Days"
                      radius={[6, 6, 0, 0]}
                      barSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Reports;
